/**
 * Pure Berechnungs-Engine für PEPlan (Planungsrechner).
 * STRIKT ZUSTANDSLOS: Keine Imports von Zustand-Stores, Firebase oder UI.
 * Verändert niemals Eingabe-Objekte (100 % Pure Functions).
 */
import { calculateConvergedUnternehmerStundenlohn } from './unternehmerlohnConvergence';
import { foerderSatz, foerderzuschussEinesMitarbeiters } from './foerderzuschuss';

// ============================================================================
// 0. GLOBALE EINSTELLUNGEN & READ-ONLY BASELINE SNAPSHOT
// ============================================================================

export interface PlanningGlobalSettings {
  mwstSatzPercent: number;               // z. B. 19 (DE), 20 (AT), 8.1 (CH), 22 (IT), 0 (USt-befreit)
  lohnnebenkostenPercent: number;        // AG-Satz aus Länderprofil / Team-Ø (z. B. 22.5 %)
  urlaubsausgleichPercent: number;       // Standard: 20 % (settings.global.Urlaubsausgleich)
  sicherheitsfaktorPercent: number;      // Standard: 25 % (lohnfaktorData.sicherheitsfaktor)
  verlustPercent: number;                // Standard: 15 % (settings.global.Verlust_1)
  ruecklagenSatzPercent: number;         // Standard: 10 % (clientData.liquiditaet ?? settings.global.Liquiditaet)
  wochensatz: number;                    // Standard: 4.33 (settings.global.Wochensatz)
  auslastungsfaktorPercent: number;      // Standard: 75 % (clientData.auslastungsfaktor)
  warenwirtschaftAufschlagVk: number;    // Standard: 1.30 (settings.global.Warenwirtschaftsaufschlag_VK)
  standardMaterialGrammProStunde: number;// Standard: 50 g (aus Kalkulator-Referenz)
  currencyCode: string;                  // 'EUR' | 'CHF'
  activeLand: 'DE' | 'AT' | 'CH' | 'IT';
}

export interface PlanningBaselineEmployee {
  id: string;
  name: string;
  taetigkeit: string;
  scope: 'produktiv' | 'unproduktiv' | 'azubi';
  grundgehaltBrutto: number;
  lohnzusatz: number;
  bonus: number;
  wochenstunden: number;
  monatsstunden: number;
  arbeitstageProWoche: number;
  lohnnebenkostenSatzPercent: number;
  agVollkostenMonat: number;             // totalCost inkl. AG-SV
  stundenlohnGesamt: number;
  istUmsatzMonatBrutto: number;          // Ø historischer Monatsumsatz des MA (falls vorhanden, sonst Soll)
  sollUmsatzMonatBrutto: number;         // (grundgehaltBrutto + bonus) * lohnfaktorAktiv
  /** Bei 'Unprod. Anteil': ID des verknüpften produktiven Mitarbeiters */
  linkedProductiveEmployeeId?: string;
  isGefoerdert?: boolean;
  foerderquoteProzent?: number;
  foerderArt?: 'prozent' | 'euro';
  foerderEuro?: number;
}

export interface PlanningMaterialItem {
  id: string;
  name: string;
  gebinde: number;
  ekPreis: number;
}

export interface PlanningDeviceItem {
  id: string;
  name: string;
  kostenMonatNetto: number;
  kostenProzent: number;
}

/**
 * Tiefenkopierter, unveränderlicher Snapshot der Live-Mandantenakte.
 * Keine Referenz auf Live-Objekte in useAppStore.
 */
export interface PlanningBaselineData {
  sourceMandantUid: string | null;
  sourceYear: string | null;
  capturedAt: string;
  zeitraumMonate: number;

  // Umsätze & BWA-Kosten (Monatswerte netto)
  istMonatsumsatzNetto: number;
  istMonatsumsatzBrutto: number;
  betriebskostenMonatNetto: number;      // BWA Sach-/Fixkosten ohne Personal/Material/Geräte
  positiveWerteMonatNetto: number;       // AfA / Zinserträge (kostenmindernd)
  materialkostenMonatNetto: number;
  baseMaterialQuotePercent: number;      // Material in % vom Netto-Umsatz
  geraetekostenMonatNetto: number;
  gemeinkostenGesamtMonatNetto: number;  // inkl. unprod. MA, ESt-Chef, 1/3 Azubi, abzgl. AfA
  sollumsatzMonatNetto: number;          // Gesamtkosten-Break-Even (ohne Fortzahlung/Rücklage)
  kalkSollumsatzInklFortzahlungNetto: number;

  // Personal-Baseline
  employees: PlanningBaselineEmployee[];
  produktiveLohnkostenMaOhneAzubisMonat: number;
  unproduktiveLohnkostenMaMonat: number;
  azubiKostenGesamtMonat: number;
  teamStundenlohn: number;
  summeProduktiveMonatsstundenTeam: number;

  // Inhaber-Baseline
  inhaberEntnahmeNetto: number;
  inhaberSozialkosten: number;
  inhaberEchterBedarfGesamt: number;     // Entnahme + Sozialkosten (kalk. Unternehmerlohn Basis)
  inhaberEinkommensteuerSatzPercent: number;
  inhaberEinkommensteuerMonat: number;
  inhaberProdWochenstunden: number;
  inhaberMonatsstunden: number;
  inhaberStundenlohnSoll: number;
  inhaberProdUnternehmerlohn: number;
  inhaberUnprodUnternehmerlohn: number;
  inhaberEigenumsatzBruttoMonat: number; // kalkulierterUnternehmerUmsatzBrutto

  // Faktoren & Quoten Baseline
  lohnfaktorNetto: number;
  lohnfaktorBrutto: number;
  lohnfaktorFortzahlung: number;
  lohnfaktorAktiv: number;
  kostendeckungsfaktorAktiv: number;     // aktiver KDF (HJ2 wenn HJ1 gesperrt, sonst HJ1)
  gemeinkostenfaktor: number;
  materialkostenfaktor: number;
  geraetekostenfaktor: number;
  prodLohnkostenfaktor: number;
  unprodUnternehmerlohnPercent: number;
  ueberschussMonatNetto: number;
  ueberschussProzent: number;
  inhaberErtragSollMonat: number;
  rentabilitaetSollProzent: number;

  // Kalkulator-Stammdaten (Read-Only Kopie für Sandbox-Kalkulator)
  materialListe: PlanningMaterialItem[];
  geraeteListe: PlanningDeviceItem[];
  barometerStundenfaktor: number;
  barometerStundenfaktorMitMaterial?: number;
}

// ============================================================================
// 1. PLANNING SCENARIO INPUTS (5 STEUERUNGS-BLÖCKE)
// ============================================================================

export type PlanningEmployeeRoleScope = 'produktiv' | 'azubi' | 'unproduktiv';

/** Block 1: Einzelner geplanter Neuzugang (Produktiv, Azubi oder Unproduktiv) */
export interface PlanningNewHireInput {
  id: string;
  label: string;                         // z. B. "Neue Fachkraft 1" / "Neuer Azubi" / "Rezeption"
  aktiv: boolean;
  /** Rolle im Salon: 'produktiv' (Standard), 'azubi' (2/3 Prod, 1/3 GK, 2/3 Team-Std.-Lohn) oder 'unproduktiv' (100 % GK) */
  scope?: PlanningEmployeeRoleScope;
  /** Eingabemodus für das Gehalt: 'monat' (Standard) oder 'stunde' (Stundenlohn -> rechnet Monatsbrutto hoch) */
  lohnEingabeModus?: 'monat' | 'stunde';
  /** Falls lohnEingabeModus === 'stunde': eingegebener Brutto-Stundenlohn */
  stundenlohnBrutto?: number;
  bruttoGehaltMonat: number;             // €/Monat brutto (Vergütung)
  wochenstunden: number;                 // Std./Woche
  /** Umsatzfaktor für den Gesamtumsatz (Standard 2,5 bei Produktiv, 0 bei Azubi/Unproduktiv) */
  standardLohnfaktor: number;
  /** Ob der Umsatz des neuen Mitarbeiters auf den Gesamtumsatz angerechnet wird (Standard: true bei Produktiv) */
  umsatzAnrechnen?: boolean;
  lohnnebenkostenSatzPercent?: number;   // Optionaler Override; sonst Land/Team-Standard
  isGefoerdert?: boolean;
  foerderquoteProzent?: number;
  foerderArt?: 'prozent' | 'euro';
  foerderEuro?: number;
}

/** Block 2: Bestandsmitarbeiter-Anpassung (Stunden/Gehalt/Rolle ändern ODER Abgang + Auffangquote) */
export interface PlanningDepartureInput {
  employeeId: string;                    // Referenz auf PlanningBaselineEmployee.id
  aktiv: boolean;                        // true = Mitarbeiter verlässt den Salon komplett (Abgang)
  auffangQuotePercent: number;           // 0 - 100 % (wie viel % des wegfallenden Umsatzes fängt das Team auf)
  /** Geplante neue Wochenstunden (wenn Mitarbeiter bleibt, aber mehr/weniger arbeitet) */
  customWochenstunden?: number;
  /** Geplantes neues Bruttogehalt/Monat (wenn Mitarbeiter bleibt, aber Gehalt angepasst wird) */
  customBruttoGehaltMonat?: number;
  /** Optionaler Eingabemodus im Bestand: Monatsbrutto oder Stundenlohn */
  lohnEingabeModus?: 'monat' | 'stunde';
  /** Optionaler Brutto-Stundenlohn im Bestand */
  customStundenlohnBrutto?: number;
  /** Optionaler Rollenwechsel im Szenario (z. B. Azubi wird als Geselle/Fachkraft übernommen) */
  customScope?: PlanningEmployeeRoleScope;
  /** Umsatzfaktor bei Übernahme als Fachkraft (Standard 2,5×) */
  customUmsatzFaktor?: number;
  /** Ob bei Übernahme als Fachkraft der Umsatz auf den Gesamtumsatz angerechnet wird (Standard: true) */
  customUmsatzAnrechnen?: boolean;
  /** Bei 'Unprod. Anteil': true (Standard) = gekoppelt mit Produktiv-MA, false = frei / entkoppelt */
  splitCoupled?: boolean;
}

/** Block 3: Inhaber-Stellschrauben */
export interface PlanningOwnerInput {
  bedienzeitWochenstunden: number;       // Geplante produktive Stunden am Stuhl / Woche
  verlagerungsQuotePercent: number;      // 0 - 100 % (Anteil des wegfallenden Chef-Umsatzes, den das Team übernimmt)
  kalkUnternehmerlohnMonat: number;      // Geplanter kalk. Unternehmerlohn (Bedarf netto vor ESt)
  einkommensteuerSatzPercent?: number;   // Optionaler Override ESt-Satz; sonst Baseline
}

/** Block 4A: Einzelne BWA-Kostenart-Anpassung (wie in Planungsanalyse / PEProfit) */
export interface PlanningBwaCostAdjustment {
  id: string;
  bwaKey: string;                        // z. B. "raumkosten" oder "custom_xyz"
  label: string;                         // z. B. "Raumkosten" oder "Neue Software"
  istMonatNetto: number;                 // Aktueller IST-Wert pro Monat netto
  zielMonatNetto: number;                // Geplanter neuer Zielwert pro Monat netto
  notiz?: string;
  aktiv: boolean;
}

/** Block 4B: Einzelne geplante Investition */
export interface PlanningInvestmentItem {
  id: string;
  label: string;                         // z. B. "Leasing Salon-Umbau / Neue Waschliegen"
  aktiv: boolean;
  monatsrateNetto: number;               // €/Monat netto
}

/** Block 4C: Neue Dienstleistung (Mehrumsatz + zusätzlicher Materialverbrauch) */
export interface PlanningNewServiceItem {
  id: string;
  label: string;                         // z. B. "Keratin-Behandlung / Head Spa"
  aktiv: boolean;
  zusatzUmsatzMonatBrutto: number;       // Geplanter Mehrumsatz pro Monat (Brutto)
  zusatzMaterialMonatNetto: number;      // Geplanter Mehrverbrauch Material pro Monat (Netto)
}

export interface PlanningInvestmentsInput {
  pauschaleZusatzkostenMonatNetto: number; // Direkte Schnell-Stellschraube (€/Monat netto, auch negativ für Einsparung möglich)
  /** Geplanter zusätzlicher Materialeinsatz (z. B. neue Pflegeserie / Mehrverbrauch in €/Monat netto) */
  zusatzMaterialMonatNetto?: number;
  materialNotiz?: string;
  bwaAnpassungen?: PlanningBwaCostAdjustment[];
  einzelInvestitionen: PlanningInvestmentItem[];
  neueDienstleistungen?: PlanningNewServiceItem[];
}

/** Block 5: Geplante Preisanpassung */
export interface PlanningPriceAdjustmentInput {
  geplantePreiserhoehungPercent: number; // z. B. +8.0 %
  /** Optional: Kundenschwund-Annahme in % (null = 0 % im reinen Kalkulationsmodus oder 4:1 Praxis-Option) */
  kundenSchwundPercent: number | null;
}

/** Gesamtes Input-Objekt für calculatePlanningScenario() */
export interface PlanningScenarioInputs {
  szenarioTitel: string;
  /** Block 1: Zuwachs */
  zuwachs: PlanningNewHireInput[];
  /** Block 2: Abgang & Auffangquote */
  abgaenge: PlanningDepartureInput[];
  /** Block 3: Inhaber */
  inhaber: PlanningOwnerInput;
  /** Block 4: Investitionen & Zusatzkosten */
  investitionen: PlanningInvestmentsInput;
  /** Block 5: Preisanpassung */
  preisanpassung: PlanningPriceAdjustmentInput;
}

// ============================================================================
// 2. PLANNING SCENARIO OUTPUTS
// ============================================================================

/** Spiegelung der neuen Faktoren auf das verbleibende + neue Team */
export interface PlanningEmployeeTargetOutput {
  id: string;
  name: string;
  isNewHire: boolean;
  isDeparted: boolean;
  wochenstunden: number;
  monatsstunden: number;
  bruttoGehaltMonat: number;
  agVollkostenMonat: number;
  stundenlohnGesamt: number;
  /** Bisheriger Soll-Umsatz (Brutto/Monat) unter Baseline-Lohnfaktor */
  sollUmsatzVorherBrutto: number;
  /** Neuer Soll-Umsatz (Brutto/Monat) mit neuem Lohnfaktor (bzw. Start-Faktor bei Neuzugang) */
  sollUmsatzNeuBrutto: number;
  sollUmsatzDeltaBrutto: number;
  /** Benötigter Stundensatz (Brutto) bei Ziel-Auslastung (z. B. 75 %) */
  requiredStundensatzBrutto: number;
  /** Kostendeckungs-Mindestumsatz (Brutto/Monat) mit neuem KDF */
  kostendeckungUmsatzNeuBrutto: number;
}

/** Vorher / Nachher / Delta Paar für Tabellen & KPI-Kacheln */
export interface PlanningMetricComparison {
  vorher: number;
  nachher: number;
  deltaAbsolut: number;
  deltaPercent: number;
}

export interface PlanningBeforeAfterDelta {
  monatsumsatzNetto: PlanningMetricComparison;
  monatsumsatzBrutto: PlanningMetricComparison;
  gesamtkostenSollumsatzNetto: PlanningMetricComparison;      // Break-Even ohne Rücklage
  kalkSollumsatzInklFortzahlungNetto: PlanningMetricComparison;
  gemeinkostenMonatNetto: PlanningMetricComparison;
  produktivePersonalkostenMonat: PlanningMetricComparison;
  materialkostenMonatNetto: PlanningMetricComparison;
  geraetekostenMonatNetto: PlanningMetricComparison;
  zusatzInvestitionenMonatNetto: PlanningMetricComparison;
  produktiveTeamStundenMonat: PlanningMetricComparison;
  teamStundenlohn: PlanningMetricComparison;
  lohnfaktorAktiv: PlanningMetricComparison;
  kostendeckungsfaktor: PlanningMetricComparison;
  gemeinkostenfaktorPercent: PlanningMetricComparison;
  materialkostenfaktorPercent: PlanningMetricComparison;
  geraetekostenfaktorPercent: PlanningMetricComparison;
  prodLohnkostenfaktorPercent: PlanningMetricComparison;
  ueberschussMonatNetto: PlanningMetricComparison;
  ueberschussPercent: PlanningMetricComparison;
  inhaberErtragMonat: PlanningMetricComparison;
  rentabilitaetPercent: PlanningMetricComparison;
}

export interface PlanningScenarioOutputs {
  // 1. Neue Faktoren (vollautomatisch reaktiv)
  neuerLohnfaktorNetto: number;
  neuerLohnfaktorBrutto: number;
  neuerLohnfaktorFortzahlung: number;
  neuerLohnfaktorAktiv: number;          // max(3.50, neuerLohnfaktorFortzahlung)
  neuerKostendeckungsfaktor: number;     // max(2.80, analytischer KDF für Prognose Lohn === Ziel)

  // Neue Quoten (0..1 Faktoren für den eingebetteten Kalkulator)
  neuerGemeinkostenfaktor: number;
  neuerMaterialkostenfaktor: number;
  neuerGeraetekostenfaktor: number;
  neuerProdLohnkostenfaktor: number;
  neuerUnprodUnternehmerlohnPercent: number;
  neuerTeamStundenlohn: number;
  neuerInhaberProdStundenlohn: number;

  // 2. Stundensätze auf Basis der neuen Werte (Brutto & Netto)
  stundensaetze: {
    /** Kostendeckend (ohne Rücklage) */
    ohneMaterialBreakEvenNetto: number;
    ohneMaterialBreakEvenBrutto: number;
    inklMaterialBreakEvenNetto: number;
    inklMaterialBreakEvenBrutto: number;
    /** Inkl. globaler Rücklagenbindung (Standard 10 %) */
    ohneMaterialMitRuecklageNetto: number;
    ohneMaterialMitRuecklageBrutto: number;
    inklMaterialMitRuecklageNetto: number;
    inklMaterialMitRuecklageBrutto: number;
    /** Verwendeter Material-Aufschlag pro 60 Min (z. B. 50g × Ø-EK × WaWi-Faktor) */
    materialAufschlagProStundeNetto: number;
  };

  // 3. Gesamtsalon-Umsatzziele (Monat)
  umsatzzieleMonat: {
    ruecklagenSatzPercent: number;       // z. B. 10 %
    /** OHNE Rücklagenbindung (reine Kostendeckung / Break-Even) */
    ohneRuecklageNetto: number;
    ohneRuecklageBrutto: number;
    /** Inkl. Lohnfortzahlung OHNE zusätzliche Rücklage */
    inklFortzahlungOhneRuecklageNetto: number;
    inklFortzahlungOhneRuecklageBrutto: number;
    /** MIT Rücklagenbindung (Standard 10 % aus Globaldaten) */
    mitRuecklageNetto: number;
    mitRuecklageBrutto: number;
    /** Planumsatz über neuen LohnfaktorAktiv */
    planumsatzLohnfaktorNetto: number;
    planumsatzLohnfaktorBrutto: number;
    /** Neutraler EGZ-Abzug. Nicht in Lohnfaktor, KDF, Teamlohn oder Sollumsatz eingerechnet. */
    foerderzuschussMonat: number;
    breakEvenNachFoerderungNetto: number;
    breakEvenNachFoerderungBrutto: number;
  };

  // 4. Angepasste Mitarbeiter-Sollumsätze (Bestand + Neuzugänge)
  mitarbeiterSollUmsaetze: PlanningEmployeeTargetOutput[];

  // 5. Vorher-Nachher-Delta (analog Kostenstruktur / Datenübersicht)
  vergleich: PlanningBeforeAfterDelta;

  /**
   * true = kein Mitarbeiter im Szenario (Live-Solo: 100 % prod. Unternehmerlohn, SV 0, kein LF/KDF).
   * Kippt automatisch, sobald der letzte Mitarbeiter geht oder der erste eingestellt wird.
   */
  isEffectiveSolo: boolean;
  baselineIsSolo: boolean;
  modusWechsel: 'solo-zu-team' | 'team-zu-solo' | null;

  // 6. Preiskonsequenz (inkl. 4:1 Praxis-Regel: 20 % Preiserhöhung = 5 % Kundenverlust)
  preiskonsequenz: {
    /** Erforderliche Preisanpassung in % (inkl. Kundenverlust), um exakt kostendeckend zu arbeiten (Break-Even = 0 € Verlust) */
    erforderlicheAnpassungKostendeckendPercent: number;
    /** Erforderliche Preisanpassung in % (inkl. Kundenverlust), um die Ziel-Rücklage (z. B. 10 %) / Gewinnstabilität zu erreichen */
    erforderlicheAnpassungGewinnstabilPercent: number;
    /** Im Ziel-Preisanpassungssatz bereits eingepreister Kundenverlust in % (Verhältnis 4:1 -> 20 % Preis = 5 % Kundenverlust) */
    kalkulierterKundenverlustZielPercent?: number;
    /** Aktiver Kundenschwund für die eingestellte Preiserhöhung (automatisch 1/4 oder manuell) */
    effektiverKundenSchwundPercent?: number;
    /** Deckungslücke oder Puffer nach bereits eingegebener Preiserhöhung (Block 5) in €/Monat netto */
    verbleibendeDeckungsLueckeNetto: number;
    /** Verbleibender weiterer Preisanpassungsbedarf in % nach Abzug von Block 5 */
    restAnpassungsbedarfPercent: number;
  };
}

export type CalculatePlanningScenarioFn = (
  baseline: PlanningBaselineData,
  inputs: PlanningScenarioInputs,
  globalSettings: PlanningGlobalSettings
) => PlanningScenarioOutputs;

// ============================================================================
// 3. INTERNE HELFER (PURE)
// ============================================================================

const num = (v: unknown, fallback = 0): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : fallback;
};

const clamp = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, v));

const safeDiv = (a: number, b: number): number => (b > 0 && Number.isFinite(b) ? a / b : 0);

/**
 * Beratungspraxis 4:1 — Bei 20 % Preisanpassung rechnen wir mit 5 % Kundenverlust (Schwund = Preis% / 4).
 */
export function calcAutoKundenSchwundPercent(preisAnpassungPercent: number): number {
  const p = num(preisAnpassungPercent, 0);
  return p > 0 ? p / 4 : 0;
}

/**
 * Löst nach der erforderlichen Preisanpassung p (in %) auf, sodass trotz
 * 4:1-Kundenschwund (20 % Preiserhöhung -> 5 % Kundenverlust) das Umsatzziel erreicht wird:
 * Basis * (1 + p) * (1 - p/4) = Ziel  ==>  (1 + p) * (1 - 0.25*p) = ratio
 */
function solveRequiredPriceIncreaseWithAttritionPercent(
  targetNetto: number,
  baseNetto: number,
  manualSchwundPercent: number | null | undefined
): { requiredPricePercent: number; impliedLossPercent: number } {
  if (!(baseNetto > 0) || !(targetNetto > 0)) {
    return { requiredPricePercent: 0, impliedLossPercent: 0 };
  }

  // Wenn ein manueller Kundenschwund fest vorgegeben wurde:
  if (manualSchwundPercent !== null && manualSchwundPercent !== undefined) {
    const s = clamp(num(manualSchwundPercent), 0, 95) / 100;
    const effBase = baseNetto * (1 - s);
    const req = effBase > 0 ? (targetNetto / effBase - 1) * 100 : 0;
    return { requiredPricePercent: req, impliedLossPercent: s * 100 };
  }

  const ratio = targetNetto / baseNetto;
  // Bei Preissenkungen (ratio <= 1) kein Kundenverlust durch Preiserhöhung:
  if (ratio <= 1) {
    return {
      requiredPricePercent: (ratio - 1) * 100,
      impliedLossPercent: 0,
    };
  }

  // Quadratische Gleichung: (1 + p) * (1 - 0.25*p) = ratio
  // <=> 1 + 0.75*p - 0.25*p^2 = ratio  <=>  p^2 - 3*p + 4*(ratio - 1) = 0
  // p = (3 - sqrt(9 - 16*(ratio - 1))) / 2
  const discriminant = 9 - 16 * (ratio - 1);
  if (discriminant >= 0) {
    const p = (3 - Math.sqrt(discriminant)) / 2;
    const pricePct = p * 100;
    return {
      requiredPricePercent: pricePct,
      impliedLossPercent: Math.max(0, pricePct / 4),
    };
  }

  // Fallback bei extrem großen Lücken (> +56 % Netto-Umsatzsprung): mit 15 % Schwund deckeln
  const cappedLoss = 0.15;
  const fallbackPct = (ratio / (1 - cappedLoss) - 1) * 100;
  return {
    requiredPricePercent: fallbackPct,
    impliedLossPercent: cappedLoss * 100,
  };
}

const buildComparison = (vorherRaw: number, nachherRaw: number): PlanningMetricComparison => {
  const vorher = num(vorherRaw);
  const nachher = num(nachherRaw);
  const deltaAbsolut = nachher - vorher;
  const deltaPercent = Math.abs(vorher) > 0.0001 ? (deltaAbsolut / Math.abs(vorher)) * 100 : 0;
  return {
    vorher,
    nachher,
    deltaAbsolut,
    deltaPercent,
  };
};

/**
 * Berechnet den Ø-Materialaufschlag (netto) für `gramm` (Standard 50g)
 * anhand der übergebenen Materialliste und des Warenwirtschaftsaufschlags VK (Standard 1.30).
 */
export function calculateStandardMaterialCostNetto(
  materialListe: PlanningMaterialItem[],
  gramm = 50,
  warenwirtschaftAufschlagVk = 1.3,
  activeMaterialIds?: string[]
): number {
  const list = Array.isArray(materialListe) ? materialListe : [];
  const filtered =
    Array.isArray(activeMaterialIds) && activeMaterialIds.length > 0
      ? list.filter((m) => activeMaterialIds.includes(m.id))
      : list;

  let sum100 = 0;
  let count = 0;
  for (const m of filtered) {
    const gebinde = num(m?.gebinde);
    const ek = num(m?.ekPreis);
    if (gebinde > 0 && ek > 0) {
      sum100 += (ek / gebinde) * 100;
      count++;
    }
  }
  const avgPer100g = count > 0 ? sum100 / count : 0;
  const matFaktor = warenwirtschaftAufschlagVk > 0 ? warenwirtschaftAufschlagVk : 1.3;
  return (avgPer100g / 100) * Math.max(0, num(gramm)) * matFaktor;
}

/**
 * Default-Inputs auf Basis eines Baseline-Snapshots erzeugen.
 */
export function createDefaultPlanningScenarioInputs(
  baseline?: PlanningBaselineData | null
): PlanningScenarioInputs {
  const departures: PlanningDepartureInput[] = (baseline?.employees || []).map((e) => ({
    employeeId: e.id,
    aktiv: false,
    auffangQuotePercent: 50,
    customWochenstunden: e.wochenstunden,
    customBruttoGehaltMonat: e.grundgehaltBrutto,
    lohnEingabeModus: 'monat',
  }));

  return {
    szenarioTitel: 'Szenario-Analyse',
    zuwachs: [],
    abgaenge: departures,
    inhaber: {
      bedienzeitWochenstunden: num(baseline?.inhaberProdWochenstunden, 30),
      verlagerungsQuotePercent: 0,
      kalkUnternehmerlohnMonat: num(baseline?.inhaberEchterBedarfGesamt, 3500),
      einkommensteuerSatzPercent: baseline?.inhaberEinkommensteuerSatzPercent,
    },
    investitionen: {
      pauschaleZusatzkostenMonatNetto: 0,
      zusatzMaterialMonatNetto: 0,
      materialNotiz: '',
      bwaAnpassungen: [],
      einzelInvestitionen: [],
      neueDienstleistungen: [],
    },
    preisanpassung: {
      geplantePreiserhoehungPercent: 0,
      kundenSchwundPercent: null,
    },
  };
}

// ============================================================================
// 4. HAUPTFUNKTION: calculatePlanningScenario
// ============================================================================

export const calculatePlanningScenario: CalculatePlanningScenarioFn = (
  baseline,
  inputs,
  globalSettings
): PlanningScenarioOutputs => {
  const mwstSatz = Math.max(0, num(globalSettings?.mwstSatzPercent, 19));
  const mwstFaktor = 1 + mwstSatz / 100;
  const wochensatz = num(globalSettings?.wochensatz, 4.33) || 4.33;
  const defaultLnkPercent = Math.max(0, num(globalSettings?.lohnnebenkostenPercent, 22.5));
  const urlaubsausgleichPercent = Math.max(0, num(globalSettings?.urlaubsausgleichPercent, 20));
  const sicherheitsfaktorPercent = Math.max(0, num(globalSettings?.sicherheitsfaktorPercent, 25));
  const verlustPercent = clamp(num(globalSettings?.verlustPercent, 15), 0, 80);
  const ruecklagenSatzPercent = Math.max(0, num(globalSettings?.ruecklagenSatzPercent, 10));
  const auslastungsfaktorPercent = clamp(num(globalSettings?.auslastungsfaktorPercent, 75), 10, 100);
  const wawiVk = num(globalSettings?.warenwirtschaftAufschlagVk, 1.3) || 1.3;
  const stdMatGramm = num(globalSettings?.standardMaterialGrammProStunde, 50) || 50;

  // Map für geplante Abgänge (Block 2)
  const departureMap = new Map<string, PlanningDepartureInput>();
  for (const dep of inputs?.abgaenge || []) {
    if (dep && dep.employeeId) {
      departureMap.set(dep.employeeId, dep);
    }
  }

  // --------------------------------------------------------------------------
  // BLOCK 2: BESTANDSMITARBEITER (PRODUKTIV, AZUBI & UNPRODUKTIV — INKL. ROLLENWECHSEL z.B. AZUBI -> GESELLE)
  // --------------------------------------------------------------------------
  // WICHTIG: Nur echte Azubis aus der Azubi-Tabelle (`scope === 'azubi'`) sind Azubis (2/3 Prod + 1/3 GK).
  // Wer in der Tabelle "Produktive Mitarbeiter" steht (`scope === 'produktiv'`), ist 100 % produktiv!
  const resolveBaseScope = (e: PlanningBaselineEmployee): PlanningEmployeeRoleScope => {
    if (e.scope === 'azubi') {
      return 'azubi';
    }
    if (e.scope === 'unproduktiv') {
      return 'unproduktiv';
    }
    return 'produktiv';
  };

  let verbleibendeAzubiKostenMonat = 0;
  let verbleibendeAzubiSummeStundenlohn = 0;
  let verbleibendeAzubiCount = 0;
  let verbleibendeUnprodLohnkostenMa = 0;
  let verbleibendeUnprodCount = 0;
  let verbleibendeProdAzubiCount = 0;

  let verbleibendeProdLohnkostenMaOhneAzubis = 0;
  let verbleibendeBruttoBasisKdf = 0; // Grundgehalt + Lohnzusatz (für KDF-Basis)
  let verbleibendeBruttoInklBonusPlanBasis = 0; // Grundgehalt + Bonus (für Planumsatz)
  let verbleibendeBoniGesamtInklSv = 0;
  let verbleibendeMonatsstundenMa = 0;
  let verbleibendeSummeStundenlohnMa = 0;
  let verbleibendeAnzahlProdMa = 0;

  let wegfallenderUmsatzNetto = 0;
  let aufgefangenerUmsatzAusAbgangNetto = 0;
  let zusatzUmsatzBestandMaNetto = 0;
  let anyEmployeeModified = false;
  let summeFoerderung = 0;
  let foerderInKosten = 0;

  let prodAzubiLohnkostenMonat = 0; // Produktive Azubis aus der Produktiv-Tabelle (100 % Prod-Kosten, nicht im LF-Nenner)
  const azubiEmployeesBaseline = (baseline?.employees || []).filter(
    (e) => resolveBaseScope(e) === 'azubi'
  );
  const unprodEmployeesBaseline = (baseline?.employees || []).filter(
    (e) => resolveBaseScope(e) === 'unproduktiv'
  );

  for (const ma of baseline?.employees || []) {
    const dep = departureMap.get(ma.id);
    const isDeparted = dep?.aktiv === true;
    const baseScope = resolveBaseScope(ma);
    const effScope: PlanningEmployeeRoleScope = dep?.customScope || baseScope;
    const scopeChanged = effScope !== baseScope;
    const auffangQuote = clamp(num(dep?.auffangQuotePercent, 50), 0, 100) / 100;

    const maHistUmsatzBrutto =
      num(ma.istUmsatzMonatBrutto) > 0
        ? num(ma.istUmsatzMonatBrutto)
        : num(ma.sollUmsatzMonatBrutto);
    const maHistUmsatzNetto =
      baseScope === 'produktiv' ? safeDiv(maHistUmsatzBrutto, mwstFaktor) : 0;

    // 1. Kompletter Abgang (Kündigung)
    if (isDeparted) {
      anyEmployeeModified = true;
      if (baseScope === 'produktiv') {
        wegfallenderUmsatzNetto += maHistUmsatzNetto;
        aufgefangenerUmsatzAusAbgangNetto += maHistUmsatzNetto * auffangQuote;
      }
      continue;
    }

    const baseWStd = num(ma.wochenstunden);
    const baseBrutto = num(ma.grundgehaltBrutto);
    const effWStd =
      dep?.customWochenstunden !== undefined && dep?.customWochenstunden !== null
        ? Math.max(0, num(dep.customWochenstunden))
        : baseWStd;
    const effBrutto =
      dep?.customBruttoGehaltMonat !== undefined && dep?.customBruttoGehaltMonat !== null
        ? Math.max(0, num(dep.customBruttoGehaltMonat))
        : baseBrutto;

    const hoursChanged = Math.abs(effWStd - baseWStd) >= 0.05;
    const salaryChanged = Math.abs(effBrutto - baseBrutto) >= 0.5;
    const isModified = hoursChanged || salaryChanged || scopeChanged;
    if (isModified) {
      anyEmployeeModified = true;
    }

    const lnkSatz = num(ma.lohnnebenkostenSatzPercent, defaultLnkPercent);
    const baseTotalBrutto = baseBrutto + num(ma.lohnzusatz) + num(ma.bonus);
    const agFactor =
      !scopeChanged && baseTotalBrutto > 0 && num(ma.agVollkostenMonat) > 0
        ? num(ma.agVollkostenMonat) / baseTotalBrutto
        : 1 + lnkSatz / 100;
    const newTotalBrutto = effBrutto + num(ma.lohnzusatz) + num(ma.bonus);
    const effAgVollkosten = !isModified
      ? num(ma.agVollkostenMonat)
      : newTotalBrutto * agFactor;
    const effMonatsstunden = !isModified
      ? num(ma.monatsstunden)
      : effWStd * wochensatz;
    const effStundenlohn = !isModified
      ? num(ma.stundenlohnGesamt)
      : effMonatsstunden > 0
        ? newTotalBrutto / effMonatsstunden
        : 0;

    const foerderPerson = {
      isGefoerdert: ma.isGefoerdert,
      foerderArt: ma.foerderArt,
      foerderEuro: ma.foerderEuro,
      foerderquoteProzent: ma.foerderquoteProzent,
      grundgehalt: effBrutto,
    };
    const satz = foerderSatz(foerderPerson);
    const kostenFaktor = 1 - satz;
    if (satz > 0 && effScope !== 'unproduktiv') {
      summeFoerderung += foerderzuschussEinesMitarbeiters(foerderPerson);
    } else if (satz > 0) {
      foerderInKosten += effAgVollkosten * satz;
    }

    // 2. Ziel-Rolle ist AZUBI (2/3 Prod, 1/3 GK, 2/3 im Team-Stundenlohn). Förderung erst am Schluss.
    if (effScope === 'azubi') {
      verbleibendeAzubiKostenMonat += effAgVollkosten;
      verbleibendeAzubiSummeStundenlohn += effStundenlohn;
      verbleibendeAzubiCount += 1;
      if (baseScope === 'produktiv') {
        wegfallenderUmsatzNetto += maHistUmsatzNetto;
        aufgefangenerUmsatzAusAbgangNetto += maHistUmsatzNetto * auffangQuote;
      }
      continue;
    }

    // 3. Ziel-Rolle ist UNPRODUKTIV (100 % Gemeinkosten)
    if (effScope === 'unproduktiv') {
      verbleibendeUnprodLohnkostenMa += effAgVollkosten * kostenFaktor;
      if ((ma.taetigkeit || '').trim() !== 'Unprod. Anteil') {
        verbleibendeUnprodCount += 1;
      }
      if (baseScope === 'produktiv') {
        wegfallenderUmsatzNetto += maHistUmsatzNetto;
        aufgefangenerUmsatzAusAbgangNetto += maHistUmsatzNetto * auffangQuote;
      }
      continue;
    }

    // 4. Ziel-Rolle ist PRODUKTIV (Fachkraft / übernommener Azubi als Geselle / Prod. Azubi)
    // Wenn ein Prod. Azubi in den Stunden/Gehalt erhöht wird (oder als Geselle übernommen wird),
    // arbeitet er produktiv mit und fließt voll in die produktive Basis (Nenner) ein,
    // damit zusätzliche produktive Stunden/Gehalt den Lohnfaktor senken statt erhöhen!
    const isProdAzubiUnmodified =
      !isModified &&
      baseScope === 'produktiv' &&
      ['Azubi', 'Prod. Azubi', 'produktiver Azubi'].includes((ma.taetigkeit || '').trim());

    if (isProdAzubiUnmodified) {
      prodAzubiLohnkostenMonat += effAgVollkosten;
      verbleibendeProdAzubiCount += 1;
    } else {
      verbleibendeProdLohnkostenMaOhneAzubis += effAgVollkosten;
      verbleibendeBruttoBasisKdf += effBrutto + num(ma.lohnzusatz);
      verbleibendeBruttoInklBonusPlanBasis += effBrutto + num(ma.bonus);
      verbleibendeBoniGesamtInklSv += num(ma.bonus) * (1 + lnkSatz / 100);
      verbleibendeMonatsstundenMa += effMonatsstunden;
      verbleibendeSummeStundenlohnMa += effStundenlohn;
      verbleibendeAnzahlProdMa += 1;
    }

    if (baseScope !== 'produktiv') {
      // Azubi oder Assistenz wurde als produktive Fachkraft (Geselle) übernommen!
      const faktor = Math.max(0, num(dep?.customUmsatzFaktor, 2.5));
      const anrechnen = dep?.customUmsatzAnrechnen !== false;
      if (anrechnen && faktor > 0) {
        const neuerGesellenUmsatzBrutto = effBrutto * faktor;
        zusatzUmsatzBestandMaNetto += safeDiv(neuerGesellenUmsatzBrutto, mwstFaktor);
      }
    } else if (isModified) {
      const scale =
        hoursChanged && baseWStd > 0
          ? effWStd / baseWStd
          : salaryChanged && baseBrutto > 0
            ? effBrutto / baseBrutto
            : 1;
      const neuerMaUmsatzNetto = maHistUmsatzNetto * scale;
      if (neuerMaUmsatzNetto < maHistUmsatzNetto) {
        const verlust = maHistUmsatzNetto - neuerMaUmsatzNetto;
        wegfallenderUmsatzNetto += verlust;
        aufgefangenerUmsatzAusAbgangNetto += verlust * auffangQuote;
      } else if (neuerMaUmsatzNetto > maHistUmsatzNetto) {
        zusatzUmsatzBestandMaNetto += neuerMaUmsatzNetto - maHistUmsatzNetto;
      }
    }
  }

  const nettoUmsatzVerlustAusAbgang = Math.max(
    0,
    wegfallenderUmsatzNetto - aufgefangenerUmsatzAusAbgangNetto
  );

  // --------------------------------------------------------------------------
  // BLOCK 1: ZUWACHS (PRODUKTIV, AZUBI [2/3 PROD + 1/3 GK + 2/3 STD-LOHN], UNPRODUKTIV [100% GK])
  // --------------------------------------------------------------------------
  const activeNewHires = (inputs?.zuwachs || []).filter(
    (h) => h && h.aktiv !== false && (num(h.bruttoGehaltMonat) > 0 || num(h.wochenstunden) > 0)
  );

  let zuwachsProdAgVollkostenMonat = 0;
  let zuwachsProdBruttoGesamt = 0;
  let zuwachsProdMonatsstunden = 0;
  let zuwachsProdSummeStundenlohn = 0;
  let zuwachsProdCount = 0;

  let zuwachsAzubiAgVollkostenMonat = 0;
  let zuwachsAzubiSummeStundenlohn = 0;
  let zuwachsAzubiCount = 0;

  let zuwachsUnprodAgVollkostenMonat = 0;
  let zuwachsUnprodCount = 0;

  let zuwachsErwarteterUmsatzBrutto = 0;

  for (const hire of activeNewHires) {
    const scope: PlanningEmployeeRoleScope = hire.scope || 'produktiv';
    const brutto = Math.max(0, num(hire.bruttoGehaltMonat));
    const wStd = Math.max(0, num(hire.wochenstunden));
    const mStd = wStd * wochensatz;
    const lnkSatz =
      hire.lohnnebenkostenSatzPercent !== undefined && hire.lohnnebenkostenSatzPercent !== null
        ? Math.max(0, num(hire.lohnnebenkostenSatzPercent))
        : defaultLnkPercent;
    const agVollkosten = brutto * (1 + lnkSatz / 100);
    const stdLohn = mStd > 0 ? brutto / mStd : 0;
    const hirePerson = {
      isGefoerdert: hire.isGefoerdert,
      foerderArt: hire.foerderArt,
      foerderEuro: hire.foerderEuro,
      foerderquoteProzent: hire.foerderquoteProzent,
      bruttoGehaltMonat: brutto,
    };
    const hireSatz = foerderSatz(hirePerson);
    const hireKostenFaktor = 1 - hireSatz;
    if (hireSatz > 0 && scope !== 'unproduktiv') {
      summeFoerderung += foerderzuschussEinesMitarbeiters(hirePerson);
    } else if (hireSatz > 0) {
      foerderInKosten += agVollkosten * hireSatz;
    }

    if (scope === 'unproduktiv') {
      zuwachsUnprodAgVollkostenMonat += agVollkosten * hireKostenFaktor;
      zuwachsUnprodCount += 1;
    } else if (scope === 'azubi') {
      zuwachsAzubiAgVollkostenMonat += agVollkosten;
      zuwachsAzubiSummeStundenlohn += stdLohn;
      zuwachsAzubiCount += 1;
      const startFaktor = Math.max(0, num(hire.standardLohnfaktor, 0));
      const umsatzAnrechnen = hire.umsatzAnrechnen === true && startFaktor > 0;
      if (umsatzAnrechnen) {
        zuwachsErwarteterUmsatzBrutto += brutto * startFaktor;
      }
    } else {
      // 'produktiv' (Standard)
      const startFaktor = Math.max(0, num(hire.standardLohnfaktor, 2.5));
      const umsatzAnrechnen = hire.umsatzAnrechnen !== false;
      const minSollBrutto = umsatzAnrechnen ? brutto * startFaktor : 0;

      zuwachsProdBruttoGesamt += brutto;
      zuwachsProdAgVollkostenMonat += agVollkosten;
      zuwachsProdMonatsstunden += mStd;
      zuwachsProdSummeStundenlohn += stdLohn;
      zuwachsProdCount += 1;
      zuwachsErwarteterUmsatzBrutto += minSollBrutto;
    }
  }

  const zuwachsErwarteterUmsatzNetto = safeDiv(zuwachsErwarteterUmsatzBrutto, mwstFaktor);

  // --------------------------------------------------------------------------
  // BLOCK 3: INHABER (STUNDEN, VERLAGERUNG, UNTERNEHMERLOHN)
  // --------------------------------------------------------------------------
  const baseChefWochenstunden = Math.max(0, num(baseline?.inhaberProdWochenstunden));
  const neueChefWochenstunden = Math.max(
    0,
    num(inputs?.inhaber?.bedienzeitWochenstunden, baseChefWochenstunden)
  );
  const neueChefMonatsstunden = neueChefWochenstunden * wochensatz;

  const baseBedarf = num(baseline?.inhaberEchterBedarfGesamt);
  const neuerKalkUnternehmerlohn = Math.max(
    0,
    num(inputs?.inhaber?.kalkUnternehmerlohnMonat, baseBedarf)
  );
  const estSatz =
    inputs?.inhaber?.einkommensteuerSatzPercent !== undefined &&
    inputs?.inhaber?.einkommensteuerSatzPercent !== null
      ? Math.max(0, num(inputs.inhaber.einkommensteuerSatzPercent))
      : Math.max(0, num(baseline?.inhaberEinkommensteuerSatzPercent));

  const chefHoursChanged = Math.abs(neueChefWochenstunden - baseChefWochenstunden) >= 0.05;
  const chefBedarfChanged = Math.abs(neuerKalkUnternehmerlohn - baseBedarf) >= 0.5;

  const neueInhaberEinkommensteuerMonat =
    !chefBedarfChanged &&
    Math.abs(estSatz - num(baseline?.inhaberEinkommensteuerSatzPercent)) < 0.05
      ? num(baseline?.inhaberEinkommensteuerMonat)
      : neuerKalkUnternehmerlohn * (estSatz / 100);

  // Aufteilung prod. vs. unprod. Unternehmerlohn
  const totalProdMaCount = verbleibendeAnzahlProdMa + zuwachsProdCount;
  const totalAzubiCount = verbleibendeAzubiCount + zuwachsAzubiCount;
  const totalUnprodCount = verbleibendeUnprodCount + zuwachsUnprodCount;
  // Live-Akte: ohne Mitarbeiter ist der Inhaber 100 % produktiv, ohne Lohnfaktor/KDF und ohne AG-Lohnnebenkosten.
  const isEffectiveSolo =
    totalProdMaCount === 0 &&
    verbleibendeProdAzubiCount === 0 &&
    totalAzubiCount === 0 &&
    totalUnprodCount === 0;
  const baselineIsSolo = !(baseline?.employees || []).some(
    (e) => (e.taetigkeit || '').trim() !== 'Unprod. Anteil'
  );
  const modusWechsel: 'solo-zu-team' | 'team-zu-solo' | null =
    baselineIsSolo && !isEffectiveSolo
      ? 'solo-zu-team'
      : !baselineIsSolo && isEffectiveSolo
        ? 'team-zu-solo'
        : null;

  // Stundenlohn-Soll des Inhabers skaliert proportional mit einer Änderung des kalk. Unternehmerlohns
  const bedarfScale = baseBedarf > 0 ? neuerKalkUnternehmerlohn / baseBedarf : 1;
  const baseChefStundenlohnSoll =
    num(baseline?.inhaberStundenlohnSoll) > 0
      ? num(baseline?.inhaberStundenlohnSoll)
      : num(baseline?.inhaberMonatsstunden) > 0
        ? safeDiv(num(baseline?.inhaberProdUnternehmerlohn), num(baseline?.inhaberMonatsstunden))
        : 0;
  const effektiverChefStundenlohnSoll = baseChefStundenlohnSoll * bedarfScale;

  let neuerProdUnternehmerlohn = 0;
  let neuerUnprodUnternehmerlohn = 0;

  if (!chefHoursChanged && !chefBedarfChanged && !isEffectiveSolo) {
    neuerProdUnternehmerlohn = num(baseline?.inhaberProdUnternehmerlohn);
    neuerUnprodUnternehmerlohn = num(baseline?.inhaberUnprodUnternehmerlohn);
  } else if (isEffectiveSolo) {
    neuerProdUnternehmerlohn = neuerKalkUnternehmerlohn;
    neuerUnprodUnternehmerlohn = 0;
  } else if (!chefHoursChanged && chefBedarfChanged) {
    neuerProdUnternehmerlohn = num(baseline?.inhaberProdUnternehmerlohn) * bedarfScale;
    neuerUnprodUnternehmerlohn = Math.max(
      0,
      neuerKalkUnternehmerlohn - neuerProdUnternehmerlohn
    );
  } else if (neueChefMonatsstunden > 0 && effektiverChefStundenlohnSoll > 0) {
    const rawProdUl = neueChefMonatsstunden * effektiverChefStundenlohnSoll;
    neuerProdUnternehmerlohn = Math.min(neuerKalkUnternehmerlohn, rawProdUl);
    neuerUnprodUnternehmerlohn = Math.max(0, neuerKalkUnternehmerlohn - neuerProdUnternehmerlohn);
  } else if (neueChefMonatsstunden <= 0 && baseChefWochenstunden > 0) {
    neuerProdUnternehmerlohn = 0;
    neuerUnprodUnternehmerlohn = neuerKalkUnternehmerlohn;
  } else {
    neuerProdUnternehmerlohn = neuerKalkUnternehmerlohn;
    neuerUnprodUnternehmerlohn = 0;
  }

  // Inhaber-Eigenumsatz & Verlagerung auf das Team
  const baseChefEigenumsatzBrutto = Math.max(0, num(baseline?.inhaberEigenumsatzBruttoMonat));
  const stundenVerhaeltnisChef = !chefHoursChanged
    ? 1
    : baseChefWochenstunden > 0
      ? neueChefWochenstunden / baseChefWochenstunden
      : neueChefWochenstunden > 0
        ? 1
        : 0;
  const neuerChefEigenumsatzBrutto = baseChefEigenumsatzBrutto * stundenVerhaeltnisChef;
  const chefUmsatzDeltaBrutto = neuerChefEigenumsatzBrutto - baseChefEigenumsatzBrutto;

  const verlagerungsQuote = clamp(num(inputs?.inhaber?.verlagerungsQuotePercent, 0), 0, 100) / 100;
  let chefNettoEffektAufSalonBrutto = chefUmsatzDeltaBrutto;
  if (chefUmsatzDeltaBrutto < 0) {
    // Reduziert der Inhaber seine Stuhlzeit, fängt das Team `verlagerungsQuote` % auf
    const wegfallChefBrutto = Math.abs(chefUmsatzDeltaBrutto);
    const vomTeamAufgefangenBrutto = wegfallChefBrutto * verlagerungsQuote;
    chefNettoEffektAufSalonBrutto = -(wegfallChefBrutto - vomTeamAufgefangenBrutto);
  }
  const chefNettoEffektAufSalonNetto = safeDiv(chefNettoEffektAufSalonBrutto, mwstFaktor);

  // --------------------------------------------------------------------------
  // BLOCK 4: BWA-KOSTENARTEN, INVESTITIONEN, NEUE DIENSTLEISTUNGEN & MATERIAL
  // --------------------------------------------------------------------------
  const pauschaleZusatzkosten = num(inputs?.investitionen?.pauschaleZusatzkostenMonatNetto, 0);
  const bwaDeltaSumme = (inputs?.investitionen?.bwaAnpassungen || [])
    .filter((b) => b && b.aktiv)
    .reduce((sum, b) => sum + (num(b.zielMonatNetto) - num(b.istMonatNetto)), 0);
  const einzelInvestSumme = (inputs?.investitionen?.einzelInvestitionen || [])
    .filter((inv) => inv && inv.aktiv)
    .reduce((sum, inv) => sum + num(inv.monatsrateNetto), 0);
  const zusatzInvestitionenMonatNetto =
    pauschaleZusatzkosten + bwaDeltaSumme + einzelInvestSumme;

  const aktiveNeueDienstleistungen = (inputs?.investitionen?.neueDienstleistungen || []).filter(
    (dl) => dl && dl.aktiv
  );
  const zusatzUmsatzDienstleistungenBrutto = aktiveNeueDienstleistungen.reduce(
    (sum, dl) => sum + Math.max(0, num(dl.zusatzUmsatzMonatBrutto)),
    0
  );
  const zusatzUmsatzDienstleistungenNetto = safeDiv(
    zusatzUmsatzDienstleistungenBrutto,
    mwstFaktor
  );
  const zusatzMaterialDienstleistungenNetto = aktiveNeueDienstleistungen.reduce(
    (sum, dl) => sum + Math.max(0, num(dl.zusatzMaterialMonatNetto)),
    0
  );

  const zusatzMaterialMonatNetto =
    num(inputs?.investitionen?.zusatzMaterialMonatNetto, 0) +
    zusatzMaterialDienstleistungenNetto;

  // --------------------------------------------------------------------------
  // BLOCK 5: PREISANPASSUNG & PROGNOSE-UMSATZ
  // --------------------------------------------------------------------------
  const basisUmsatzVorPreisNetto = Math.max(
    0,
    num(baseline?.istMonatsumsatzNetto) -
      nettoUmsatzVerlustAusAbgang +
      zusatzUmsatzBestandMaNetto +
      zuwachsErwarteterUmsatzNetto +
      chefNettoEffektAufSalonNetto +
      zusatzUmsatzDienstleistungenNetto
  );

  const geplantePreiserhoehungPercent = num(
    inputs?.preisanpassung?.geplantePreiserhoehungPercent,
    0
  );
  // Automatischer Kundenschwund nach 4:1-Regel (20 % Preiserhöhung = 5 % Kundenverlust), sofern nicht manuell überschrieben
  const autoKundenSchwundPercent = calcAutoKundenSchwundPercent(geplantePreiserhoehungPercent);
  const kundenSchwundPercent =
    inputs?.preisanpassung?.kundenSchwundPercent !== null &&
    inputs?.preisanpassung?.kundenSchwundPercent !== undefined
      ? clamp(num(inputs.preisanpassung.kundenSchwundPercent), 0, 100)
      : autoKundenSchwundPercent;

  const isScenarioNeutral =
    !anyEmployeeModified &&
    activeNewHires.length === 0 &&
    !chefHoursChanged &&
    !chefBedarfChanged &&
    Math.abs(zusatzInvestitionenMonatNetto) < 0.01 &&
    Math.abs(zusatzMaterialMonatNetto) < 0.01 &&
    Math.abs(zusatzUmsatzDienstleistungenNetto) < 0.01 &&
    Math.abs(geplantePreiserhoehungPercent) < 0.01 &&
    kundenSchwundPercent < 0.01;

  const umsatzNachPreiserhoehungNetto = isScenarioNeutral
    ? num(baseline?.istMonatsumsatzNetto)
    : basisUmsatzVorPreisNetto *
      (1 + geplantePreiserhoehungPercent / 100) *
      (1 - kundenSchwundPercent / 100);
  const umsatzNachPreiserhoehungBrutto = isScenarioNeutral
    ? num(baseline?.istMonatsumsatzBrutto)
    : umsatzNachPreiserhoehungNetto * mwstFaktor;

  // --------------------------------------------------------------------------
  // 5. NEUE KOSTENBASIS, LOHNFAKTOR & KDF (REAKTIV)
  // --------------------------------------------------------------------------
  const azubiKostenGesamt =
    (azubiEmployeesBaseline.length > 0
      ? verbleibendeAzubiKostenMonat
      : Math.max(0, num(baseline?.azubiKostenGesamtMonat))) + zuwachsAzubiAgVollkostenMonat;
  const unprodLohnkostenMa =
    (unprodEmployeesBaseline.length > 0
      ? verbleibendeUnprodLohnkostenMa
      : Math.max(0, num(baseline?.unproduktiveLohnkostenMaMonat))) + zuwachsUnprodAgVollkostenMonat;
  const betriebskostenBasis = Math.max(0, num(baseline?.betriebskostenMonatNetto));
  const positiveWerteMonat = Math.max(0, num(baseline?.positiveWerteMonatNetto));
  const geraetekostenMonatNetto = Math.max(0, num(baseline?.geraetekostenMonatNetto));

  const neueGemeinkostenMonatNetto = isScenarioNeutral
    ? num(baseline?.gemeinkostenGesamtMonatNetto)
    : Math.max(
        0,
        betriebskostenBasis +
          zusatzInvestitionenMonatNetto +
          unprodLohnkostenMa +
          neueInhaberEinkommensteuerMonat +
          azubiKostenGesamt * (1 / 3) -
          positiveWerteMonat
      );

  const neueProdLohnkostenMaOhneAzubis =
    verbleibendeProdLohnkostenMaOhneAzubis + zuwachsProdAgVollkostenMonat;

  // Materialkosten: Basis + mengenmäßige Veränderung durch Personal/Inhaber-Umsatz (mit aktivem Materialkostenfaktor) + geplantes Zusatzmaterial (z. B. neue Pflegeserie)
  const baseMatQuote =
    num(baseline?.materialkostenfaktor) > 0
      ? num(baseline?.materialkostenfaktor)
      : Math.max(0, num(baseline?.baseMaterialQuotePercent)) / 100;
  const mengenUmsatzDeltaNetto =
    zuwachsErwarteterUmsatzNetto +
    zusatzUmsatzBestandMaNetto -
    nettoUmsatzVerlustAusAbgang +
    chefNettoEffektAufSalonNetto;
  const neueMaterialkostenMonatNetto = isScenarioNeutral
    ? num(baseline?.materialkostenMonatNetto)
    : Math.max(
        0,
        num(baseline?.materialkostenMonatNetto) +
          mengenUmsatzDeltaNetto * baseMatQuote +
          zusatzMaterialMonatNetto
      );

  // Wie LohnfaktorPage: im Szenario konvergiert der Unternehmer-Stundenlohn gegen die neue Team-/Kostenstruktur,
  // damit prodUnternehmerlohn nicht auf dem alten Vorher-Wert eingefroren bleibt (sonst klebt LF bei 3,50 trotz KDF > 3,50).
  const sollumsatzOhneUnternehmerlohn =
    neueGemeinkostenMonatNetto +
    neueProdLohnkostenMaOhneAzubis +
    prodAzubiLohnkostenMonat +
    azubiKostenGesamt * (2 / 3) +
    neueMaterialkostenMonatNetto +
    geraetekostenMonatNetto;

  if (!isScenarioNeutral && neueChefMonatsstunden > 0 && neuerKalkUnternehmerlohn > 0) {
    const convergedChefSoll = calculateConvergedUnternehmerStundenlohn({
      isSolo: isEffectiveSolo,
      echterBedarfGesamt: neuerKalkUnternehmerlohn,
      chefMonatsstunden: neueChefMonatsstunden,
      sollumsatzOhneUnternehmerlohn,
      produktiveLohnkostenMitarbeiterOhneAzubis: neueProdLohnkostenMaOhneAzubis,
      lohnfaktorNennerMitarbeiterOhneAzubis: neueProdLohnkostenMaOhneAzubis,
      kalkulierterUnternehmerUmsatzBrutto: neuerChefEigenumsatzBrutto,
      mwstSatz,
      sicherheitsfaktorPercent,
      urlaubsausgleichPercent,
      initialGuess: effektiverChefStundenlohnSoll > 0 ? effektiverChefStundenlohnSoll : undefined,
    });
    if (convergedChefSoll > 0) {
      if (isEffectiveSolo) {
        neuerProdUnternehmerlohn = neuerKalkUnternehmerlohn;
        neuerUnprodUnternehmerlohn = 0;
      } else {
        const rawProdUl = neueChefMonatsstunden * convergedChefSoll;
        neuerProdUnternehmerlohn = Math.min(neuerKalkUnternehmerlohn, rawProdUl);
        neuerUnprodUnternehmerlohn = Math.max(0, neuerKalkUnternehmerlohn - neuerProdUnternehmerlohn);
      }
    }
  }

  const neuerInhaberProdStundenlohn =
    neueChefMonatsstunden > 0
      ? safeDiv(neuerProdUnternehmerlohn, neueChefMonatsstunden)
      : num(baseline?.inhaberStundenlohnSoll);

  const neueProduktiveKostenGesamtMonat =
    neueProdLohnkostenMaOhneAzubis +
    prodAzubiLohnkostenMonat +
    neuerProdUnternehmerlohn +
    azubiKostenGesamt * (2 / 3);

  // Neuer Sollumsatz (Break-Even ohne Fortzahlung / ohne Rücklage)
  const neuerSollumsatzNetto = isScenarioNeutral
    ? num(baseline?.sollumsatzMonatNetto)
    : neueGemeinkostenMonatNetto +
      neueProduktiveKostenGesamtMonat +
      neueMaterialkostenMonatNetto +
      geraetekostenMonatNetto +
      neuerUnprodUnternehmerlohn;

  // Lohnfortzahlung (Urlaubsausgleich auf produktive MA-Vollkosten ohne Azubis)
  const neueLohnfortzahlung =
    neueProdLohnkostenMaOhneAzubis * (urlaubsausgleichPercent / 100);
  const neuerKalkSollumsatzInklFortzahlungNetto = isScenarioNeutral
    ? num(baseline?.kalkSollumsatzInklFortzahlungNetto)
    : neuerSollumsatzNetto + neueLohnfortzahlung;

  // Produktive Kosten Tabelle (Nenner für Lohnfaktor Netto)
  const neueProduktiveKostenTabelle =
    neueProdLohnkostenMaOhneAzubis + neuerProdUnternehmerlohn;

  // Wenn die Preise erhöht werden (abzgl. 4:1-Kundenschwund), steigt der Erlös pro Leistung.
  // Dadurch sinkt virtuell auch der Multiplikator (Lohnfaktor / KDF), den das Team auf den Grundlohn
  // erbringen muss, im Verhältnis der Netto-Erlössteigerung (bis zum Floor 3,50 bzw. 2,80).
  const nettoPreisErlosHebel =
    !isScenarioNeutral && Math.abs(geplantePreiserhoehungPercent) >= 0.01
      ? Math.max(
          0.25,
          (1 + geplantePreiserhoehungPercent / 100) * (1 - kundenSchwundPercent / 100)
        )
      : 1;

  const neuerLohnfaktorNettoRaw = isScenarioNeutral
    ? num(baseline?.lohnfaktorNetto)
    : neueProduktiveKostenTabelle > 0
      ? neuerKalkSollumsatzInklFortzahlungNetto / neueProduktiveKostenTabelle
      : 0;
  const neuerLohnfaktorNetto = safeDiv(neuerLohnfaktorNettoRaw, nettoPreisErlosHebel);
  const neuerLohnfaktorBrutto = isScenarioNeutral
    ? num(baseline?.lohnfaktorBrutto)
    : neuerLohnfaktorNetto * mwstFaktor;
  const neuerLohnfaktorFortzahlung = isScenarioNeutral
    ? num(baseline?.lohnfaktorFortzahlung)
    : neuerLohnfaktorBrutto * (1 + sicherheitsfaktorPercent / 100);

  // Automatischer Kostendeckungsfaktor (KDF): Prognose Lohn === Ziel (Floor 2.80)
  const basisUmsatzKostendeckung = verbleibendeBruttoBasisKdf + zuwachsProdBruttoGesamt;
  const gesamtUnternehmerlohn = neuerProdUnternehmerlohn + neuerUnprodUnternehmerlohn;
  const gesamtzielBereinigt =
    neuerSollumsatzNetto - gesamtUnternehmerlohn - verbleibendeBoniGesamtInklSv;

  let neuerKostendeckungsfaktor = Math.max(2.8, num(baseline?.kostendeckungsfaktorAktiv, 2.8));
  if (!isScenarioNeutral && basisUmsatzKostendeckung > 0 && gesamtUnternehmerlohn > 0) {
    const kdfExact =
      ((gesamtUnternehmerlohn + gesamtzielBereinigt) * mwstFaktor -
        neuerChefEigenumsatzBrutto) /
      basisUmsatzKostendeckung /
      nettoPreisErlosHebel;
    if (Number.isFinite(kdfExact)) {
      neuerKostendeckungsfaktor = Number(Math.max(2.8, kdfExact).toFixed(2));
    }
  }

  // Eisernes Gesetz: Lohnfaktor aktiv niemals unter 3,50 — und im Szenario niemals unter dem KDF!
  const neuerLohnfaktorAktiv = isScenarioNeutral
    ? Math.max(3.5, num(baseline?.lohnfaktorAktiv, 3.5))
    : Math.max(3.5, neuerLohnfaktorFortzahlung, neuerKostendeckungsfaktor);

  // Team-Stundenlohn & Produktive Teamstunden (Azubis fließen zu 2/3 in den Team-Stundenlohn ein)
  const summeAzubiStundenlohnAnteil =
    (verbleibendeAzubiSummeStundenlohn + zuwachsAzubiSummeStundenlohn) * (2 / 3);
  const anzahlChefProd = neuerProdUnternehmerlohn > 0 ? 1 : 0;
  const anzahlProduktivesTeamGesamt =
    verbleibendeAnzahlProdMa +
    zuwachsProdCount +
    anzahlChefProd +
    totalAzubiCount;

  const neuerTeamStundenlohn = isScenarioNeutral
    ? num(baseline?.teamStundenlohn)
    : anzahlProduktivesTeamGesamt > 0
      ? (verbleibendeSummeStundenlohnMa +
          zuwachsProdSummeStundenlohn +
          neuerInhaberProdStundenlohn +
          summeAzubiStundenlohnAnteil) /
        anzahlProduktivesTeamGesamt
      : neuerInhaberProdStundenlohn;

  const neueProduktiveTeamStundenMonat = isScenarioNeutral
    ? num(baseline?.summeProduktiveMonatsstundenTeam)
    : verbleibendeMonatsstundenMa +
      zuwachsProdMonatsstunden +
      (anzahlChefProd > 0 ? neueChefMonatsstunden : 0);

  // Neue Faktoren (0..1) bezogen auf den Szenario-Monatsumsatz Netto (Fallback: neuerSollumsatzNetto)
  const referenzUmsatzFuerQuotenNetto =
    umsatzNachPreiserhoehungNetto > 0 ? umsatzNachPreiserhoehungNetto : neuerSollumsatzNetto;

  const neuerGemeinkostenfaktor = isScenarioNeutral
    ? num(baseline?.gemeinkostenfaktor)
    : safeDiv(neueGemeinkostenMonatNetto, referenzUmsatzFuerQuotenNetto);
  const neuerMaterialkostenfaktor = isScenarioNeutral
    ? num(baseline?.materialkostenfaktor)
    : safeDiv(neueMaterialkostenMonatNetto, referenzUmsatzFuerQuotenNetto);
  const neuerGeraetekostenfaktor = isScenarioNeutral
    ? num(baseline?.geraetekostenfaktor)
    : safeDiv(geraetekostenMonatNetto, referenzUmsatzFuerQuotenNetto);
  const neuerProdLohnkostenfaktor = isScenarioNeutral
    ? num(baseline?.prodLohnkostenfaktor)
    : safeDiv(neueProduktiveKostenGesamtMonat, referenzUmsatzFuerQuotenNetto);
  const neuerUnprodUnternehmerlohnPercent = isScenarioNeutral
    ? num(baseline?.unprodUnternehmerlohnPercent)
    : referenzUmsatzFuerQuotenNetto > 0
      ? (neuerUnprodUnternehmerlohn / referenzUmsatzFuerQuotenNetto) * 100
      : 0;

  // --------------------------------------------------------------------------
  // 6. GESAMTSALON-UMSATZZIELE & PREISKONSEQUENZ (EINHEITLICHE BASIS MIT KALK. ÜBERSCHUSS!)
  // --------------------------------------------------------------------------
  // WICHTIG: Damit oben „Erforderliche Preisanpassung für 10 % Rücklage“ und unten
  // „Kalk. Überschuss / Verlust (Ziel mind. 10 %)“ zu 100 % synchron laufen,
  // beziehen sich beide auf dieselbe Kostenbasis `neuerSollumsatzNetto` (wie in KostenstrukturPage):
  // Bei Ziel-Umsatz gilt: (Umsatz - Sollumsatz) / Umsatz === ruecklagenSatzPercent (z. B. 10 %),
  // also Ziel-Umsatz = Sollumsatz / (1 - ruecklagenSatzPercent / 100).
  const ruecklagenQuote = clamp(ruecklagenSatzPercent / 100, 0, 0.8);

  const ohneRuecklageNetto = neuerSollumsatzNetto;
  const ohneRuecklageBrutto = ohneRuecklageNetto * mwstFaktor;

  const inklFortzahlungOhneRuecklageNetto = neuerSollumsatzNetto;
  const inklFortzahlungOhneRuecklageBrutto = ohneRuecklageBrutto;

  const mitRuecklageNetto =
    ruecklagenQuote < 0.95
      ? safeDiv(neuerSollumsatzNetto, 1 - ruecklagenQuote)
      : neuerSollumsatzNetto * (1 + ruecklagenQuote);
  const mitRuecklageBrutto = mitRuecklageNetto * mwstFaktor;
  const foerderzuschussMonat = Math.max(0, summeFoerderung + foerderInKosten);
  const breakEvenNachFoerderungNetto = Math.max(0, neuerSollumsatzNetto - summeFoerderung);
  const breakEvenNachFoerderungBrutto = breakEvenNachFoerderungNetto * mwstFaktor;

  const planumsatzBasisNeu =
    verbleibendeBruttoInklBonusPlanBasis + zuwachsProdBruttoGesamt + neuerProdUnternehmerlohn;
  const planumsatzLohnfaktorBrutto = planumsatzBasisNeu * neuerLohnfaktorAktiv;
  const planumsatzLohnfaktorNetto = safeDiv(planumsatzLohnfaktorBrutto, mwstFaktor);

  // Erforderliche Preisanpassung INKL. 4:1-Kundenverlust (20 % Preiserhöhung = 5 % Kundenverlust)
  const solvedBreakEven = solveRequiredPriceIncreaseWithAttritionPercent(
    ohneRuecklageNetto,
    basisUmsatzVorPreisNetto,
    inputs?.preisanpassung?.kundenSchwundPercent
  );
  const erforderlicheAnpassungKostendeckendPercent = solvedBreakEven.requiredPricePercent;

  const zielUmsatzGewinnstabilNetto = mitRuecklageNetto;
  const solvedGewinnstabil = solveRequiredPriceIncreaseWithAttritionPercent(
    zielUmsatzGewinnstabilNetto,
    basisUmsatzVorPreisNetto,
    inputs?.preisanpassung?.kundenSchwundPercent
  );
  const erforderlicheAnpassungGewinnstabilPercent = solvedGewinnstabil.requiredPricePercent;
  const kalkulierterKundenverlustZielPercent = solvedGewinnstabil.impliedLossPercent;

  const verbleibendeDeckungsLueckeNetto =
    zielUmsatzGewinnstabilNetto - umsatzNachPreiserhoehungNetto;

  const solvedRest = solveRequiredPriceIncreaseWithAttritionPercent(
    zielUmsatzGewinnstabilNetto,
    umsatzNachPreiserhoehungNetto,
    inputs?.preisanpassung?.kundenSchwundPercent
  );
  const restAnpassungsbedarfPercent = solvedRest.requiredPricePercent;

  // --------------------------------------------------------------------------
  // 7. STUNDENSÄTZE (DIREKT VERANKERT AN DEN KALKULATOR-STUNDENSÄTZEN & PREISERHÖHUNG)
  // --------------------------------------------------------------------------
  const materialAufschlagProStundeNetto = calculateStandardMaterialCostNetto(
    baseline?.materialListe || [],
    stdMatGramm,
    wawiVk
  );

  const effectiveSvPercent = isEffectiveSolo ? 0 : defaultLnkPercent;
  const lohnBasisStunde = neuerTeamStundenlohn;
  const urlaubStunde = lohnBasisStunde * (urlaubsausgleichPercent / 100);
  const svStunde = (lohnBasisStunde + urlaubStunde) * (effectiveSvPercent / 100);
  const vollLohnStundeNetto = lohnBasisStunde + urlaubStunde + svStunde;

  const propAbzugOhneRuecklage =
    neuerGemeinkostenfaktor +
    neuerUnprodUnternehmerlohnPercent / 100 +
    verlustPercent / 100;
  const nennerBreakEven = Math.max(0.15, 1 - propAbzugOhneRuecklage);
  const nennerMitRuecklage = Math.max(
    0.1,
    1 - propAbzugOhneRuecklage - ruecklagenSatzPercent / 100
  );

  const analytischNeuOhneMatNetto = safeDiv(vollLohnStundeNetto, nennerMitRuecklage);
  const analytischNeuInklMatNetto = safeDiv(
    vollLohnStundeNetto + materialAufschlagProStundeNetto,
    nennerMitRuecklage
  );

  const storedBaseBrutto = num(baseline?.barometerStundenfaktor);
  const storedMatBrutto = num(baseline?.barometerStundenfaktorMitMaterial);

  const ohneMaterialBreakEvenNetto = safeDiv(vollLohnStundeNetto, nennerBreakEven);
  const ohneMaterialBreakEvenBrutto =
    storedBaseBrutto > 0
      ? storedBaseBrutto
      : ohneMaterialBreakEvenNetto * mwstFaktor;

  const inklMaterialBreakEvenNetto = safeDiv(
    vollLohnStundeNetto + materialAufschlagProStundeNetto,
    nennerBreakEven
  );
  const inklMaterialBreakEvenBrutto =
    storedMatBrutto > 0
      ? storedMatBrutto
      : inklMaterialBreakEvenNetto * mwstFaktor;

  // Wenn der Nutzer eine Preiserhöhung einstellt (z. B. +14,5 %), steigen die Stundensätze exakt um +14,5 %!
  // Wenn keine manuelle Preiserhöhung gesetzt ist, aber Personal/Kosten/Inhaber verändert wurden,
  // zeigt „Neu im Szenario“ den erforderlichen Stundensatz passend zur erforderlichen Preisanpassung.
  const hasExplicitPriceChange = Math.abs(geplantePreiserhoehungPercent) >= 0.01;
  const stundensatzPreisFaktor = isScenarioNeutral
    ? 1
    : hasExplicitPriceChange
      ? Math.max(0, 1 + geplantePreiserhoehungPercent / 100)
      : Math.max(0, 1 + erforderlicheAnpassungGewinnstabilPercent / 100);

  const ohneMaterialMitRuecklageBrutto =
    storedBaseBrutto > 0
      ? storedBaseBrutto * stundensatzPreisFaktor
      : analytischNeuOhneMatNetto * mwstFaktor * (hasExplicitPriceChange ? stundensatzPreisFaktor : 1);
  const ohneMaterialMitRuecklageNetto = safeDiv(ohneMaterialMitRuecklageBrutto, mwstFaktor);

  const inklMaterialMitRuecklageBrutto =
    storedMatBrutto > 0
      ? storedMatBrutto * stundensatzPreisFaktor
      : analytischNeuInklMatNetto * mwstFaktor * (hasExplicitPriceChange ? stundensatzPreisFaktor : 1);
  const inklMaterialMitRuecklageNetto = safeDiv(inklMaterialMitRuecklageBrutto, mwstFaktor);

  // --------------------------------------------------------------------------
  // 8. MITARBEITER-SOLLUMSÄTZE (SPIEGELUNG AUF BESTAND + NEUZUGÄNGE)
  // --------------------------------------------------------------------------
  const mitarbeiterSollUmsaetze: PlanningEmployeeTargetOutput[] = [];
  const auslastungQuote = auslastungsfaktorPercent / 100;

  for (const ma of baseline?.employees || []) {
    const dep = departureMap.get(ma.id);
    const baseScope = resolveBaseScope(ma);
    const effScope: PlanningEmployeeRoleScope = dep?.customScope || baseScope;
    if (effScope !== 'produktiv' && baseScope !== 'produktiv') {
      continue;
    }
    const isDeparted = dep?.aktiv === true || effScope !== 'produktiv';
    const effWStd = isDeparted
      ? 0
      : dep?.customWochenstunden !== undefined && dep?.customWochenstunden !== null
        ? Math.max(0, num(dep.customWochenstunden))
        : num(ma.wochenstunden);
    const effBrutto = isDeparted
      ? 0
      : dep?.customBruttoGehaltMonat !== undefined && dep?.customBruttoGehaltMonat !== null
        ? Math.max(0, num(dep.customBruttoGehaltMonat))
        : num(ma.grundgehaltBrutto);
    const effMonatsstunden = effWStd * wochensatz;

    const lnkSatz = num(ma.lohnnebenkostenSatzPercent, defaultLnkPercent);
    const baseTotalBrutto = num(ma.grundgehaltBrutto) + num(ma.lohnzusatz) + num(ma.bonus);
    const agFactor =
      baseScope === 'produktiv' && baseTotalBrutto > 0 && num(ma.agVollkostenMonat) > 0
        ? num(ma.agVollkostenMonat) / baseTotalBrutto
        : 1 + lnkSatz / 100;
    const effTotalBrutto = effBrutto + num(ma.lohnzusatz) + num(ma.bonus);
    const effAgVollkosten = isDeparted ? 0 : effTotalBrutto * agFactor;
    const effStundenlohn =
      !isDeparted && effMonatsstunden > 0 ? effTotalBrutto / effMonatsstunden : 0;

    const grundlohnVorherInklBonus = num(ma.grundgehaltBrutto) + num(ma.bonus);
    const grundlohnNeuInklBonus = effBrutto + num(ma.bonus);
    const sollVorherBrutto =
      baseScope === 'produktiv'
        ? num(ma.sollUmsatzMonatBrutto) ||
          grundlohnVorherInklBonus * Math.max(3.5, num(baseline?.lohnfaktorAktiv, 3.5))
        : effBrutto * Math.max(0, num(dep?.customUmsatzFaktor, 2.5));
    const sollNeuBrutto = isDeparted ? 0 : grundlohnNeuInklBonus * neuerLohnfaktorAktiv;
    const sollUmsatzStd =
      !isDeparted && effMonatsstunden > 0 ? sollNeuBrutto / effMonatsstunden : 0;
    const requiredStundensatzBrutto =
      !isDeparted && auslastungQuote > 0 ? sollUmsatzStd / auslastungQuote : 0;
    const kostendeckungUmsatzNeuBrutto = isDeparted
      ? 0
      : (effBrutto + num(ma.lohnzusatz)) * neuerKostendeckungsfaktor;

    mitarbeiterSollUmsaetze.push({
      id: ma.id,
      name: baseScope !== 'produktiv' && effScope === 'produktiv' ? `${ma.name} (Übernahme)` : ma.name,
      isNewHire: baseScope !== 'produktiv' && effScope === 'produktiv',
      isDeparted,
      wochenstunden: effWStd,
      monatsstunden: effMonatsstunden,
      bruttoGehaltMonat: effBrutto,
      agVollkostenMonat: effAgVollkosten,
      stundenlohnGesamt: effStundenlohn,
      sollUmsatzVorherBrutto: sollVorherBrutto,
      sollUmsatzNeuBrutto: sollNeuBrutto,
      sollUmsatzDeltaBrutto: sollNeuBrutto - sollVorherBrutto,
      requiredStundensatzBrutto,
      kostendeckungUmsatzNeuBrutto,
    });
  }

  for (const hire of activeNewHires) {
    if ((hire.scope || 'produktiv') !== 'produktiv') continue;
    const brutto = Math.max(0, num(hire.bruttoGehaltMonat));
    const wStd = Math.max(0, num(hire.wochenstunden));
    const mStd = wStd * wochensatz;
    const lnkSatz =
      hire.lohnnebenkostenSatzPercent !== undefined && hire.lohnnebenkostenSatzPercent !== null
        ? Math.max(0, num(hire.lohnnebenkostenSatzPercent))
        : defaultLnkPercent;
    const agVollkosten = brutto * (1 + lnkSatz / 100);
    const stdLohn = mStd > 0 ? brutto / mStd : 0;
    const startFaktor = Math.max(0, num(hire.standardLohnfaktor, 2.5));
    const sollVorherBrutto = hire.umsatzAnrechnen !== false ? brutto * startFaktor : 0; // Geplanter Einstiegs-Umsatz (Standard 2,5×)
    const sollNeuBrutto = brutto * neuerLohnfaktorAktiv; // Ziel-Sollumsatz unter neuem Salon-Lohnfaktor
    const sollUmsatzStd = mStd > 0 ? sollNeuBrutto / mStd : 0;
    const requiredStundensatzBrutto =
      auslastungQuote > 0 ? sollUmsatzStd / auslastungQuote : 0;
    const kostendeckungUmsatzNeuBrutto = brutto * neuerKostendeckungsfaktor;

    mitarbeiterSollUmsaetze.push({
      id: hire.id,
      name: hire.label || 'Neue Fachkraft',
      isNewHire: true,
      isDeparted: false,
      wochenstunden: wStd,
      monatsstunden: mStd,
      bruttoGehaltMonat: brutto,
      agVollkostenMonat: agVollkosten,
      stundenlohnGesamt: stdLohn,
      sollUmsatzVorherBrutto: sollVorherBrutto,
      sollUmsatzNeuBrutto: sollNeuBrutto,
      sollUmsatzDeltaBrutto: sollNeuBrutto - sollVorherBrutto,
      requiredStundensatzBrutto,
      kostendeckungUmsatzNeuBrutto,
    });
  }

  // --------------------------------------------------------------------------
  // 9. ÜBERSCHUSS, RENTABILITÄT & VORHER-NACHHER-VERGLEICH
  // --------------------------------------------------------------------------
  const neuerUeberschussNetto = umsatzNachPreiserhoehungNetto - neuerSollumsatzNetto;
  const neuerUeberschussProzent =
    umsatzNachPreiserhoehungNetto > 0
      ? (neuerUeberschussNetto / umsatzNachPreiserhoehungNetto) * 100
      : 0;

  let neuerInhaberErtragMonat = 0;
  if (neuerUeberschussNetto >= 0) {
    neuerInhaberErtragMonat =
      neuerUeberschussNetto + neuerKalkUnternehmerlohn + neueInhaberEinkommensteuerMonat;
  } else {
    const auszahlbarerLohn = neuerKalkUnternehmerlohn + neuerUeberschussNetto;
    if (auszahlbarerLohn >= 0) {
      const steuerEffektiv = auszahlbarerLohn * (estSatz / 100);
      neuerInhaberErtragMonat = auszahlbarerLohn + steuerEffektiv;
    } else {
      neuerInhaberErtragMonat = auszahlbarerLohn;
    }
  }

  const neueRentabilitaetPercent =
    umsatzNachPreiserhoehungNetto > 0
      ? (neuerInhaberErtragMonat / umsatzNachPreiserhoehungNetto) * 100
      : 0;

  const baseProdPersonalkosten =
    num(baseline?.produktiveLohnkostenMaOhneAzubisMonat) +
    num(baseline?.inhaberProdUnternehmerlohn) +
    num(baseline?.azubiKostenGesamtMonat) * (2 / 3);

  const vergleich: PlanningBeforeAfterDelta = {
    monatsumsatzNetto: buildComparison(
      num(baseline?.istMonatsumsatzNetto),
      umsatzNachPreiserhoehungNetto
    ),
    monatsumsatzBrutto: buildComparison(
      num(baseline?.istMonatsumsatzBrutto),
      umsatzNachPreiserhoehungBrutto
    ),
    gesamtkostenSollumsatzNetto: buildComparison(
      num(baseline?.sollumsatzMonatNetto),
      neuerSollumsatzNetto
    ),
    kalkSollumsatzInklFortzahlungNetto: buildComparison(
      num(baseline?.kalkSollumsatzInklFortzahlungNetto),
      neuerKalkSollumsatzInklFortzahlungNetto
    ),
    gemeinkostenMonatNetto: buildComparison(
      num(baseline?.gemeinkostenGesamtMonatNetto),
      neueGemeinkostenMonatNetto
    ),
    produktivePersonalkostenMonat: buildComparison(
      baseProdPersonalkosten,
      neueProduktiveKostenGesamtMonat
    ),
    materialkostenMonatNetto: buildComparison(
      num(baseline?.materialkostenMonatNetto),
      neueMaterialkostenMonatNetto
    ),
    geraetekostenMonatNetto: buildComparison(
      num(baseline?.geraetekostenMonatNetto),
      geraetekostenMonatNetto
    ),
    zusatzInvestitionenMonatNetto: buildComparison(0, zusatzInvestitionenMonatNetto),
    produktiveTeamStundenMonat: buildComparison(
      num(baseline?.summeProduktiveMonatsstundenTeam),
      neueProduktiveTeamStundenMonat
    ),
    teamStundenlohn: buildComparison(num(baseline?.teamStundenlohn), neuerTeamStundenlohn),
    lohnfaktorAktiv: buildComparison(num(baseline?.lohnfaktorAktiv), neuerLohnfaktorAktiv),
    kostendeckungsfaktor: buildComparison(
      num(baseline?.kostendeckungsfaktorAktiv, 2.8),
      neuerKostendeckungsfaktor
    ),
    gemeinkostenfaktorPercent: buildComparison(
      num(baseline?.gemeinkostenfaktor) * 100,
      neuerGemeinkostenfaktor * 100
    ),
    materialkostenfaktorPercent: buildComparison(
      num(baseline?.materialkostenfaktor) * 100,
      neuerMaterialkostenfaktor * 100
    ),
    geraetekostenfaktorPercent: buildComparison(
      num(baseline?.geraetekostenfaktor) * 100,
      neuerGeraetekostenfaktor * 100
    ),
    prodLohnkostenfaktorPercent: buildComparison(
      num(baseline?.prodLohnkostenfaktor) * 100,
      neuerProdLohnkostenfaktor * 100
    ),
    ueberschussMonatNetto: buildComparison(
      num(baseline?.ueberschussMonatNetto),
      neuerUeberschussNetto
    ),
    ueberschussPercent: buildComparison(
      num(baseline?.ueberschussProzent),
      neuerUeberschussProzent
    ),
    inhaberErtragMonat: buildComparison(
      num(baseline?.inhaberErtragSollMonat),
      neuerInhaberErtragMonat
    ),
    rentabilitaetPercent: buildComparison(
      num(baseline?.rentabilitaetSollProzent),
      neueRentabilitaetPercent
    ),
  };

  return {
    neuerLohnfaktorNetto,
    neuerLohnfaktorBrutto,
    neuerLohnfaktorFortzahlung,
    neuerLohnfaktorAktiv,
    neuerKostendeckungsfaktor,
    neuerGemeinkostenfaktor,
    neuerMaterialkostenfaktor,
    neuerGeraetekostenfaktor,
    neuerProdLohnkostenfaktor,
    neuerUnprodUnternehmerlohnPercent,
    neuerTeamStundenlohn,
    neuerInhaberProdStundenlohn,
    stundensaetze: {
      ohneMaterialBreakEvenNetto,
      ohneMaterialBreakEvenBrutto,
      inklMaterialBreakEvenNetto,
      inklMaterialBreakEvenBrutto,
      ohneMaterialMitRuecklageNetto,
      ohneMaterialMitRuecklageBrutto,
      inklMaterialMitRuecklageNetto,
      inklMaterialMitRuecklageBrutto,
      materialAufschlagProStundeNetto,
    },
    umsatzzieleMonat: {
      ruecklagenSatzPercent,
      ohneRuecklageNetto,
      ohneRuecklageBrutto,
      inklFortzahlungOhneRuecklageNetto,
      inklFortzahlungOhneRuecklageBrutto,
      mitRuecklageNetto,
      mitRuecklageBrutto,
      planumsatzLohnfaktorNetto,
      planumsatzLohnfaktorBrutto,
      foerderzuschussMonat,
      breakEvenNachFoerderungNetto,
      breakEvenNachFoerderungBrutto,
    },
    mitarbeiterSollUmsaetze,
    vergleich,
    isEffectiveSolo,
    baselineIsSolo,
    modusWechsel,
    preiskonsequenz: {
      erforderlicheAnpassungKostendeckendPercent,
      erforderlicheAnpassungGewinnstabilPercent,
      kalkulierterKundenverlustZielPercent,
      effektiverKundenSchwundPercent: kundenSchwundPercent,
      verbleibendeDeckungsLueckeNetto,
      restAnpassungsbedarfPercent,
    },
  };
};

// ============================================================================
// 5. PURE KALKULATOR-FUNKTION FÜR DIENSTLEISTUNGEN IN DER SANDBOX
// ============================================================================

export interface PlanningServiceKalkulatorResult {
  mwstValue: number;
  nettoNachMwst: number;
  totalMaterialCost: number;
  totalMaterialCostPercent: number;
  totalActivatedDevicePrice: number;
  totalActivatedDevicePricePercent: number;
  gemeinkostenValue: number;
  unternehmerlohnValue: number;
  verlustValue: number;
  uebrigFuerLohn: number;
  lohnkostenValue: number;
  gewinn: number;
  gewinnPercent: number;
  currentAnpassungPercent: number;
  empfohlenerBreakEvenBrutto: number;
  empfohlenerZielPreisMitRuecklageBrutto: number;
}

/**
 * Berechnet eine konkrete Dienstleistung im eingebetteten Kalkulator auf Basis
 * der simulierten Szenario-Outputs (1:1 Kaskade wie KalkulatorPage / SoloSimulatorPage).
 */
export function calculatePlanningServiceKalkulation(opts: {
  bisherigerPreisBrutto: number;
  neuerPreisBrutto: number;
  bedienzeitMinuten: number;
  materialPreisNetto: number;
  materialGramm: number;
  activeMaterialIds: string[];
  activeDeviceIds: string[];
  materialListe: PlanningMaterialItem[];
  geraeteListe: PlanningDeviceItem[];
  outputs: PlanningScenarioOutputs;
  globalSettings: PlanningGlobalSettings;
  isEffectiveSolo?: boolean;
}): PlanningServiceKalkulatorResult {
  const mwst = Math.max(0, num(opts.globalSettings?.mwstSatzPercent, 19)) / 100;
  const gk = Math.max(0, num(opts.outputs?.neuerGemeinkostenfaktor));
  const ul = Math.max(0, num(opts.outputs?.neuerUnprodUnternehmerlohnPercent)) / 100;
  const vl = Math.max(0, num(opts.globalSettings?.verlustPercent, 15)) / 100;
  const ruecklage = Math.max(0, num(opts.globalSettings?.ruecklagenSatzPercent, 10)) / 100;
  const matFaktor =
    num(opts.globalSettings?.warenwirtschaftAufschlagVk, 1.3) > 0
      ? num(opts.globalSettings?.warenwirtschaftAufschlagVk, 1.3)
      : 1.3;

  const matGrammCostNetto = calculateStandardMaterialCostNetto(
    opts.materialListe || [],
    opts.materialGramm,
    matFaktor,
    opts.activeMaterialIds
  );
  const matCost = Number(
    (matGrammCostNetto + Math.max(0, num(opts.materialPreisNetto)) * matFaktor).toFixed(2)
  );

  let dPerc = 0;
  const activeDevSet = new Set(opts.activeDeviceIds || []);
  for (const d of opts.geraeteListe || []) {
    if (d && activeDevSet.has(d.id)) {
      dPerc += num(d.kostenProzent);
    }
  }

  const neuerPreis = Math.max(0, num(opts.neuerPreisBrutto));
  const bisherigerPreis = Math.max(0, num(opts.bisherigerPreisBrutto));
  const mwstVal = neuerPreis - neuerPreis / (1 + mwst);
  const netto = neuerPreis - mwstVal;

  const devCostTotal = netto * (dPerc / 100);
  const gemeinkostenValue = netto * gk;
  const unternehmerlohnValue = netto * ul;
  const verlustValue = netto * vl;

  const uebrig = Number(
    (
      netto -
      matCost -
      devCostTotal -
      gemeinkostenValue -
      unternehmerlohnValue -
      verlustValue
    ).toFixed(2)
  );

  const bedienzeit = num(opts.bedienzeitMinuten) > 0 ? num(opts.bedienzeitMinuten) : 60;
  const inhaberStd = num(opts.outputs?.neuerInhaberProdStundenlohn);
  const stundenlohnBasis = opts.isEffectiveSolo
    ? inhaberStd > 0
      ? inhaberStd
      : num(opts.outputs?.neuerTeamStundenlohn)
    : num(opts.outputs?.neuerTeamStundenlohn);
  const lohnBasis = Number((stundenlohnBasis * (bedienzeit / 60)).toFixed(2));
  const urlaubsPercent = Math.max(0, num(opts.globalSettings?.urlaubsausgleichPercent, 20));
  const urlaubVal = Number((lohnBasis * (urlaubsPercent / 100)).toFixed(2));
  const svPercent = opts.isEffectiveSolo
    ? 0
    : Math.max(0, num(opts.globalSettings?.lohnnebenkostenPercent, 22.5));
  const sozialVal = Number(((lohnBasis + urlaubVal) * (svPercent / 100)).toFixed(2));
  const totalLohn = Number((lohnBasis + urlaubVal + sozialVal).toFixed(2));

  const finalGewinn = Number((uebrig - totalLohn).toFixed(2));
  const gewinnPercent = netto > 0 ? (finalGewinn / netto) * 100 : 0;
  const currentAnpassungPercent =
    bisherigerPreis > 0 ? (neuerPreis / bisherigerPreis - 1) * 100 : 0;

  // Empfohlene Preise (Break-Even & inkl. Rücklage)
  const factorBreakEven = 1 - dPerc / 100 - gk - ul - vl;
  const factorMitRuecklage = factorBreakEven - ruecklage;

  const empfohlenerBreakEvenBrutto =
    factorBreakEven > 0.01
      ? Number((((totalLohn + matCost) / factorBreakEven) * (1 + mwst)).toFixed(2))
      : 0;

  const empfohlenerZielPreisMitRuecklageBrutto =
    factorMitRuecklage > 0.01
      ? Number((((totalLohn + matCost) / factorMitRuecklage) * (1 + mwst)).toFixed(2))
      : 0;

  return {
    mwstValue: mwstVal,
    nettoNachMwst: netto,
    totalMaterialCost: matCost,
    totalMaterialCostPercent: netto > 0 ? (matCost / netto) * 100 : 0,
    totalActivatedDevicePrice: devCostTotal,
    totalActivatedDevicePricePercent: dPerc,
    gemeinkostenValue,
    unternehmerlohnValue,
    verlustValue,
    uebrigFuerLohn: uebrig,
    lohnkostenValue: totalLohn,
    gewinn: finalGewinn,
    gewinnPercent,
    currentAnpassungPercent,
    empfohlenerBreakEvenBrutto,
    empfohlenerZielPreisMitRuecklageBrutto,
  };
}
